import React, { useState, useEffect } from 'react';
import { getCurrentUser, updateUserProfile, unlinkTelegram, UserResponse } from '../services/api';

const ProfilePage = () => {
    const [user, setUser] = useState<UserResponse | null>(null);
    const [formData, setFormData] = useState({
        full_name: '',
        phone: '',
        nationality: '',
        assistant_name: '',
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    useEffect(() => {
        const fetchUser = async () => {
            try {
                const currentUser = await getCurrentUser();
                if (currentUser) {
                    setUser(currentUser);
                    setFormData({
                        full_name: currentUser.full_name,
                        phone: currentUser.phone || '', // Need to add phone/nationality to UserResponse interface if missing
                        nationality: currentUser.nationality || '',
                        assistant_name: currentUser.assistant_name,
                    });
                }
            } catch (err: any) {
                setMessage({ type: 'error', text: 'Error cargando perfil.' });
            } finally {
                setLoading(false);
            }
        };
        fetchUser();
    }, []);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSaving(true);
        setMessage(null);
        try {
            await updateUserProfile(formData);
            setMessage({ type: 'success', text: 'Perfil actualizado correctamente.' });
            // Refresh user data
            const updatedUser = await getCurrentUser();
            if (updatedUser) setUser(updatedUser);
        } catch (err: any) {
            setMessage({ type: 'error', text: 'Error actualizando perfil: ' + err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleUnlinkTelegram = async () => {
        if (!window.confirm("¿Estás seguro de que deseas desvincular tu cuenta de Telegram? Dejarás de recibir respuestas del asistente por esa vía.")) return;
        try {
            await unlinkTelegram();
            setMessage({ type: 'success', text: 'Telegram desvinculado exitosamente.' });
            // Refresh user data
            const updatedUser = await getCurrentUser();
            if (updatedUser) setUser(updatedUser);
        } catch (err: any) {
            setMessage({ type: 'error', text: 'Error al desvincular Telegram: ' + err.message });
        }
    };

    if (loading) return <div className="container py-5">Cargando...</div>;
    if (!user) return <div className="container py-5">No se encontró usuario.</div>;

    return (
        <div className="light-theme-page">
            <div className="container py-5">
                <div className="row justify-content-center">
                    <div className="col-lg-6">
                        <div className="card shadow-sm">
                            <div className="card-body p-4">
                                <h3 className="card-title mb-4">Mi Perfil</h3>

                                {message && (
                                    <div className={`alert alert-${message.type === 'success' ? 'success' : 'danger'}`}>
                                        {message.text}
                                    </div>
                                )}

                                <form onSubmit={handleSubmit}>
                                    <div className="mb-3">
                                        <label className="form-label">Nombre Completo</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="full_name"
                                            value={formData.full_name}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Email (No editable)</label>
                                        <input type="email" className="form-control" value={user.email} disabled />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Nombre del Asistente</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="assistant_name"
                                            value={formData.assistant_name}
                                            onChange={handleChange}
                                            placeholder="Ej: Mi Asistente"
                                        />
                                        <div className="form-text">Este es el nombre que usará tu bot en Telegram.</div>
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Teléfono</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label className="form-label">Nacionalidad</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="nationality"
                                            value={formData.nationality}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <div className="d-grid mt-4">
                                        <button type="submit" className="btn btn-primary" disabled={saving}>
                                            {saving ? 'Guardando...' : 'Guardar Cambios'}
                                        </button>
                                    </div>
                                </form>

                                <hr className="my-5" />

                                <h4 className="card-title mb-3">Integraciones</h4>
                                <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
                                    <div className="d-flex align-items-center">
                                        <i className={`bi bi-telegram fs-2 me-3 ${user.telegram_id ? 'text-primary' : 'text-muted'}`}></i>
                                        <div>
                                            <h6 className="mb-0">Telegram</h6>
                                            <small className="text-muted">
                                                {user.telegram_id ? 'Cuenta vinculada' : 'No conectado'}
                                            </small>
                                        </div>
                                    </div>
                                    {user.telegram_id && (
                                        <button onClick={handleUnlinkTelegram} className="btn btn-sm btn-outline-danger">
                                            Desvincular
                                        </button>
                                    )}
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
